import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const Offers: React.FC = () => {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cream-50">

      {/* ===== Hero — Bold Editorial ===== */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gold-400/10 via-cream-50 to-forest-700/5 pointer-events-none" />
        <div className="relative section-container py-20 sm:py-28 lg:py-36">
          <div className="max-w-3xl">
            <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-6 block">Exclusive Offers</span>
            <h1 className="font-display text-display-lg sm:text-display-xl lg:text-display-2xl text-forest-900 tracking-tight mb-6">
              Save more.<br />
              <span className="text-gradient">Every order.</span>
            </h1>
            <p className="text-body-lg text-warm-500 max-w-lg">
              Coupons, referral rewards, and review bonuses — multiple ways to save on every purchase.
            </p>
          </div>
        </div>
      </section>

      {/* ===== Bento Grid — Coupons + Referral ===== */}
      <section className="section-container pb-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Coupon cards — 3 stacked in col 1-2 */}
          {[
            { badge: 'New Member', amount: '$10', desc: 'Welcome discount issued automatically upon sign-up', color: 'from-gold-400 to-gold-500' },
            { badge: 'Returning', amount: '$10', desc: 'Thank-you discount available from your second purchase', color: 'from-forest-700 to-forest-800' },
            { badge: 'Community', amount: '$10', desc: 'Exclusive discount for verified community members', color: 'from-forest-800 to-forest-900' },
          ].map((coupon, i) => (
            <div
              key={i}
              className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${coupon.color} text-cream-100 p-8 sm:p-10 ${i === 0 ? 'lg:col-span-2 lg:row-span-2' : ''}`}
            >
              {/* Background pattern */}
              <div className="absolute inset-0 pointer-events-none opacity-10">
                <div className="absolute top-[-20%] right-[-10%] w-[60%] h-[120%] bg-white/20 rounded-full blur-3xl" />
              </div>

              <div className="relative">
                <span className="inline-block text-[11px] font-bold bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full mb-6 uppercase tracking-wider">
                  {coupon.badge}
                </span>

                {i === 0 ? (
                  <>
                    <div className="flex items-baseline gap-3 mb-4">
                      <span className="font-display text-[5rem] sm:text-[7rem] leading-none tracking-tighter">{coupon.amount}</span>
                      <span className="text-heading-lg font-semibold opacity-70">OFF</span>
                    </div>
                    <p className="text-body-lg text-cream-100/70 max-w-sm">{coupon.desc}</p>
                    <div className="mt-8">
                      <Link to="/signup" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-forest-900 text-[15px] font-semibold rounded-lg hover:bg-warm-200 transition-all duration-300 ease-spring">
                        Sign Up Now
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                        </svg>
                      </Link>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2 mb-3">
                      <span className="font-display text-display-md tracking-tighter">{coupon.amount}</span>
                      <span className="text-subheading font-semibold opacity-70">OFF</span>
                    </div>
                    <p className="text-body-sm text-cream-100/60">{coupon.desc}</p>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ===== Referral — Full-width Split ===== */}
      <section className="bg-cream-100/60">
        <div className="section-container py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">

            {/* Left — Big text */}
            <div>
              <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-6 block">Invite & Earn</span>
              <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight mb-6">
                Refer a friend,<br />get <span className="text-gold-400">$10</span> each.
              </h2>
              <p className="text-body text-warm-500 mb-10 max-w-md">
                Share your unique referral link. When your friend makes their first purchase, you both receive a $10 discount coupon.
              </p>
              <Link to="/mypage" className="btn-primary gap-2.5">
                Get Your Referral Link
                <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
            </div>

            {/* Right — Steps as vertical timeline */}
            <div className="space-y-0">
              {[
                { step: '01', title: 'Share your link', desc: 'Copy your unique referral link from My Page and share it with friends.', icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                )},
                { step: '02', title: 'Friend signs up & orders', desc: 'They get an instant $10 welcome coupon on their first order.', icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                )},
                { step: '03', title: 'You get rewarded', desc: 'Your $10 coupon is automatically issued after their purchase.', icon: (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )},
              ].map((item, i) => (
                <div key={item.step} className="flex gap-6">
                  {/* Timeline */}
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-cream-50 border-2 border-forest-200 rounded-xl flex items-center justify-center text-gold-400 shadow-card flex-shrink-0">
                      {item.icon}
                    </div>
                    {i < 2 && <div className="w-0.5 h-full bg-forest-100 my-1" />}
                  </div>
                  {/* Content */}
                  <div className="pb-10">
                    <span className="text-nano font-bold text-gold-400 tracking-wider">{item.step}</span>
                    <h4 className="font-display text-subheading text-forest-900 mb-1.5 tracking-tight">{item.title}</h4>
                    <p className="text-body-sm text-warm-500">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Review Rewards — 3 glass cards ===== */}
      <section className="section-container py-20 lg:py-28">
        <div className="text-center mb-14">
          <span className="text-nano font-semibold text-forest-700 uppercase tracking-[0.2em] mb-4 block">Earn Credits</span>
          <h2 className="font-display text-display-sm sm:text-display-md text-forest-900 tracking-tight">Review Rewards</h2>
          <p className="text-body text-warm-500 mt-3">Share your experience and earn store credit on every review.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 max-w-4xl mx-auto">
          {[
            { type: 'Text Review', amount: '$5', desc: 'Write about your experience', accent: 'gold-400', popular: false },
            { type: 'Photo Review', amount: '$10', desc: 'Include a photo for extra credit', accent: 'gold-400', popular: true },
            { type: 'Best Review', amount: '$25', desc: 'Monthly best review bonus', accent: 'amber-500', popular: false },
          ].map((reward, i) => (
            <div
              key={i}
              className={`relative bg-cream-50 rounded-2xl p-8 text-center transition-all duration-500 ease-spring hover:shadow-hover hover:-translate-y-1 ${
                reward.popular
                  ? 'border-2 border-forest-300 shadow-card'
                  : 'border border-warm-200'
              }`}
            >
              {reward.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold-400 text-forest-900 text-[10px] font-bold px-4 py-1 rounded-full uppercase tracking-wider">
                  Popular
                </span>
              )}
              <div className={`font-display text-[3.5rem] leading-none text-${reward.accent} tracking-tighter mb-3`}>
                {reward.amount}
              </div>
              <h3 className="font-display text-subheading text-forest-900 mb-2 tracking-tight">{reward.type}</h3>
              <p className="text-body-sm text-warm-500">{reward.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/review/write" className="btn-accent gap-2">
            Write a Review
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>

      {/* ===== Free Shipping ===== */}
      <section className="bg-cream-100/60">
        <div className="section-container py-16 lg:py-20">
          <div className="flex flex-col sm:flex-row items-center gap-6 sm:gap-10 max-w-2xl mx-auto">
            <div className="w-16 h-16 rounded-2xl bg-forest-50 border border-forest-200 flex items-center justify-center flex-shrink-0">
              <svg className="w-7 h-7 text-forest-600" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.079-.481 1.09-1.102a17.09 17.09 0 00-1.305-6.148l-1.57-3.14a3.375 3.375 0 00-3.015-1.86H12.75M9.75 6.75H3.375c-.621 0-1.125.504-1.125 1.125v8.25c0 .621.504 1.125 1.125 1.125h.375" />
              </svg>
            </div>
            <div className="text-center sm:text-left">
              <h3 className="font-display text-heading-md text-forest-900 tracking-tight mb-1.5">Free Worldwide Shipping</h3>
              <p className="text-body-sm text-warm-500">Orders over <strong className="text-forest-700">$150</strong> ship free to any destination. Discreet packaging on every order.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Terms — Clean accordion ===== */}
      <section className="border-t border-warm-200">
        <div className="section-container py-8">
          <button
            onClick={() => setTermsOpen(!termsOpen)}
            className="w-full flex items-center justify-between py-3 group cursor-pointer"
          >
            <div className="flex items-center gap-2.5">
              <svg className="w-4 h-4 text-warm-400" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-caption font-semibold text-warm-600 group-hover:text-forest-900 transition-colors">Terms & Conditions</span>
            </div>
            <svg className={`w-4 h-4 text-warm-400 transition-transform duration-300 ${termsOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {termsOpen && (
            <ul className="space-y-3 text-body-sm text-warm-500 leading-relaxed pt-2 pb-6 pl-7">
              <li className="flex gap-3">
                <span className="w-1 h-1 bg-warm-300 rounded-full mt-2.5 flex-shrink-0" />
                <span>Coupons are limited to <strong className="text-forest-700">one per order</strong> and cannot be combined with other promotions.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 bg-warm-300 rounded-full mt-2.5 flex-shrink-0" />
                <span>Referral coupon is issued after the referred friend <strong className="text-forest-700">completes their first order</strong>.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 bg-warm-300 rounded-full mt-2.5 flex-shrink-0" />
                <span>Review rewards are issued after admin approval. Inappropriate content may be excluded.</span>
              </li>
              <li className="flex gap-3">
                <span className="w-1 h-1 bg-warm-300 rounded-full mt-2.5 flex-shrink-0" />
                <span>Event details are subject to change without prior notice.</span>
              </li>
            </ul>
          )}
        </div>
      </section>

      {/* ===== Bottom CTA ===== */}
      <section className="bg-cream-100/60">
        <div className="section-container py-20 text-center">
          <h2 className="font-display text-display-sm text-forest-900 tracking-tight mb-4">Ready to save?</h2>
          <p className="text-body text-warm-500 mb-8">Browse our selection and apply your rewards at checkout.</p>
          <Link to="/shop" className="btn-primary gap-2.5">
            Start Shopping
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Offers;
